import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyAuth } from '@/contexts/FamilyAuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Users, Plus, Trash2, Upload, ArrowLeft, Star } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactsManagementPage({ seniorId, seniorName, onClose }) {
  const { user, household } = useFamilyAuth();
  const [contacts, setContacts] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newContact, setNewContact] = useState({
    family_user_id: '',
    name: '',
    relationship: '',
    image_url: null,
    is_favorite: false
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (seniorId && household?.id) {
      fetchContacts();
      fetchFamilyMembers();
    }
  }, [seniorId, household]);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/api/seniors/${seniorId}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Fehler beim Laden der Kontakte');
    } finally {
      setLoading(false);
    }
  };

  const fetchFamilyMembers = async () => {
    try {
      const response = await axios.get(`${API}/api/households/${household.id}/family`);
      setFamilyMembers(response.data);
    } catch (error) {
      console.error('Error fetching family members:', error);
    }
  };

  const handleAddContact = async () => {
    if (!newContact.family_user_id || !newContact.name || !newContact.relationship) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }

    try {
      await axios.post(
        `${API}/api/seniors/${seniorId}/contacts`,
        newContact
      );
      
      toast.success('Kontakt hinzugefügt');
      setShowAddModal(false);
      setNewContact({
        family_user_id: '',
        name: '',
        relationship: '',
        image_url: null,
        is_favorite: false
      });
      fetchContacts();
    } catch (error) {
      console.error('Error adding contact:', error);
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const handleImageUpload = async (contactId, file) => {
    if (!file) return;

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/api/seniors/${seniorId}/contacts/${contactId}/upload-image`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      toast.success('Bild hochgeladen');
      fetchContacts();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!confirm('Kontakt wirklich löschen?')) return;

    try {
      await axios.delete(`${API}/api/seniors/${seniorId}/contacts/${contactId}`);
      toast.success('Kontakt gelöscht');
      fetchContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const toggleFavorite = async (contact) => {
    try {
      await axios.put(
        `${API}/api/seniors/${seniorId}/contacts/${contact.id}`,
        { is_favorite: !contact.is_favorite }
      );
      fetchContacts();
    } catch (error) {
      console.error('Error updating favorite:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F1E] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-violet-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F1E] pb-20">
      {/* Header */}
      <div className="sticky top-0 bg-gradient-to-r from-[#1a1f3a] to-[#0f1425] border-b border-white/5 backdrop-blur-xl z-10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-3 hover:bg-white/5 rounded-xl transition-colors"
                >
                  <ArrowLeft className="w-6 h-6 text-gray-400" />
                </button>
              )}
              <div>
                <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                  <Users className="w-8 h-8 text-violet-400" />
                  Kontakte für {seniorName || 'Senior'}
                </h1>
                <p className="text-gray-400 mt-1">
                  Familienmitglieder hinzufügen, die {seniorName || 'der Senior'} anrufen kann
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white rounded-xl font-semibold flex items-center gap-2 transition-all"
            >
              <Plus className="w-5 h-5" />
              Kontakt hinzufügen
            </button>
          </div>
        </div>
      </div>

      {/* Contacts Grid */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {contacts.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-12 pb-12 text-center">
              <Users className="w-20 h-20 mx-auto mb-4 text-gray-600" />
              <h2 className="text-2xl font-bold text-white mb-2">Keine Kontakte</h2>
              <p className="text-gray-400 mb-6">
                Fügen Sie Familienmitglieder hinzu, damit Senioren sie einfach anrufen können
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-6 py-3 bg-violet-500 hover:bg-violet-600 text-white rounded-xl font-semibold inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Ersten Kontakt hinzufügen
              </button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {contacts.map((contact) => (
              <Card key={contact.id} className="bg-white/5 border-white/10 hover:bg-white/10 transition-all group">
                <CardContent className="pt-6 pb-6">
                  {/* Contact Image */}
                  <div className="relative mb-4">
                    <div className="w-full aspect-square rounded-2xl bg-gradient-to-br from-violet-500/20 to-purple-600/20 flex items-center justify-center overflow-hidden">
                      {contact.image_url ? (
                        <img
                          src={contact.image_url}
                          alt={contact.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Users className="w-20 h-20 text-gray-600" />
                      )}
                    </div>
                    
                    {/* Favorite Star */}
                    <button
                      onClick={() => toggleFavorite(contact)}
                      className="absolute top-2 right-2 p-2 bg-black/50 backdrop-blur rounded-full hover:bg-black/70 transition-all"
                    >
                      <Star 
                        className={`w-5 h-5 ${contact.is_favorite ? 'text-yellow-400 fill-yellow-400' : 'text-gray-400'}`} 
                      />
                    </button>

                    {/* Upload Button */}
                    <label className="absolute bottom-2 right-2 p-2 bg-violet-500 hover:bg-violet-600 rounded-full cursor-pointer transition-all">
                      <Upload className="w-5 h-5 text-white" />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handleImageUpload(contact.id, e.target.files[0]);
                          }
                        }}
                      />
                    </label>
                  </div>

                  {/* Contact Info */}
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-white mb-1">{contact.name}</h3>
                    <p className="text-gray-400 text-sm">{contact.relationship}</p>
                  </div>

                  {/* Delete Button */}
                  <button
                    onClick={() => handleDeleteContact(contact.id)}
                    className="w-full px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all"
                  >
                    <Trash2 className="w-4 h-4" />
                    Löschen
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add Contact Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <Card className="bg-[#1a1f3a] border-white/10 max-w-lg w-full">
            <CardContent className="pt-8 pb-8">
              <h2 className="text-2xl font-bold text-white mb-6">Neuer Kontakt</h2>
              
              <div className="space-y-4">
                {/* Family Member Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">
                    Familienmitglied
                  </label>
                  <select
                    value={newContact.family_user_id}
                    onChange={(e) => {
                      const member = familyMembers.find(m => m.id === e.target.value);
                      setNewContact({
                        ...newContact,
                        family_user_id: e.target.value,
                        name: member?.name || ''
                      });
                    }}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                  >
                    <option value="">Bitte wählen...</option>
                    {familyMembers.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name} ({member.email})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">
                    Anzeigename
                  </label>
                  <input
                    type="text"
                    value={newContact.name}
                    onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                    placeholder="z.B. Mama, Papa, Oma..."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>

                {/* Relationship */}
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">
                    Beziehung
                  </label>
                  <select
                    value={newContact.relationship}
                    onChange={(e) => setNewContact({ ...newContact, relationship: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                  >
                    <option value="">Bitte wählen...</option>
                    <option value="Sohn">Sohn / Son</option>
                    <option value="Tochter">Tochter / Daughter</option>
                    <option value="Enkel">Enkel / Grandson</option>
                    <option value="Enkelin">Enkelin / Granddaughter</option>
                    <option value="Bruder">Bruder / Brother</option>
                    <option value="Schwester">Schwester / Sister</option>
                    <option value="Neffe">Neffe / Nephew</option>
                    <option value="Nichte">Nichte / Niece</option>
                    <option value="Freund">Freund / Friend</option>
                  </select>
                </div>

                {/* Favorite */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newContact.is_favorite}
                    onChange={(e) => setNewContact({ ...newContact, is_favorite: e.target.checked })}
                    className="w-5 h-5 rounded border-white/10 bg-white/5 text-violet-500 focus:ring-violet-500"
                  />
                  <span className="text-white">Als Favorit markieren</span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-8">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 text-white rounded-xl font-semibold transition-all"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleAddContact}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white rounded-xl font-semibold transition-all"
                >
                  Hinzufügen
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
